/**
 * ScoringDashboard — Page récap des performances agents (Admin RH).
 *
 * Affiche :
 *   - Bandeau « Héros candidat du mois » + boutons Publier / Refuser (validation explicite avant page publique)
 *   - Tableau récap : score global, score par axe, statut, classement, évolution mois-1
 *   - Filtre période + boutons drill-down vers fiche scoring individuelle
 *   - Indicateurs synthétiques : moyenne équipe, nb performants / à suivre / à accompagner
 *
 * Différent de :
 *   - ScoringTab.jsx (paramétrage des barèmes + saisie individuelle)
 *   - AgentPerformanceTab.jsx (rapport multi-modules historique)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Award, Crown, TrendingUp, TrendingDown, Minus, CheckCircle2, XCircle,
  ChevronRight, RotateCcw, Eye, EyeOff, AlertCircle,
} from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const getCurrentRole = () => {
  try {
    const u = JSON.parse(localStorage.getItem('dermascan_user') || '{}');
    if (u.is_admin || u.role === 'admin') return 'admin';
    if (u.role === 'manager') return 'manager';
    return u.role || 'agent';
  } catch { return 'agent'; }
};

const AGENT_AXIS_LABELS = {
  caisse: 'Caisse', ponctualite: 'Ponct.', sadaskincare: 'SkinCare',
  sadahealth: 'Health', sadarayon: 'Rayon', sadagas: 'GAS',
  polyvalence: 'Polyv.', titulaire: 'Titul.', manager_direct: 'Mgr',
};
const CYRILLE_AXIS_LABELS = {
  resultats_equipe: 'Équipe', perf_commerciale_perso: 'Commerc.',
  discipline_equipe: 'Discipl.', disponibilite_stock: 'Stock', titulaire: 'Titul.',
};

const previousMonthPeriod = () => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const statusMeta = (score) => {
  if (score == null) return { label: 'Non évalué', cls: 'bg-zinc-100 text-zinc-500', short: '—' };
  if (score >= 80) return { label: 'Performant', cls: 'bg-emerald-100 text-emerald-700', short: '✓' };
  if (score >= 60) return { label: 'À suivre', cls: 'bg-amber-100 text-amber-700', short: '~' };
  return { label: 'À accompagner', cls: 'bg-red-100 text-red-700', short: '!' };
};

const TrendBadge = ({ delta }) => {
  if (delta == null) return <span className="text-zinc-300 text-xs">—</span>;
  if (delta > 0.5) return <span className="text-emerald-600 text-xs font-semibold flex items-center"><TrendingUp className="w-3 h-3 mr-0.5" />+{delta.toFixed(1)}</span>;
  if (delta < -0.5) return <span className="text-red-600 text-xs font-semibold flex items-center"><TrendingDown className="w-3 h-3 mr-0.5" />{delta.toFixed(1)}</span>;
  return <span className="text-zinc-500 text-xs flex items-center"><Minus className="w-3 h-3 mr-0.5" />stable</span>;
};

// ─── Bandeau validation Dashboard (visibilité Cyrille) ───
const DashboardValidationBanner = ({ period, onChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const role = getCurrentRole();
  const isAdmin = role === 'admin';

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/agent-scoring/dashboard/validation?period=${period}`, { headers: auth() });
      setData(r.data);
      onChange?.(r.data);
    } catch { /* ignore */ }
  }, [period, onChange]);

  useEffect(() => { load(); }, [load]);

  const validate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/agent-scoring/dashboard/validate`, { period }, { headers: auth() });
      toast.success('Dashboard validé · accessible à Cyrille');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };
  const unvalidate = async () => {
    if (!window.confirm('Retirer la validation ? Cyrille n\'aura plus accès au dashboard pour cette période.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/agent-scoring/dashboard/unvalidate`, { period }, { headers: auth() });
      toast.success('Validation retirée');
      await load();
    } catch (e) { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  if (!isAdmin) return null;
  const isValidated = data?.status === 'validated';
  return (
    <div className={`rounded-lg p-3 border-2 ${isValidated ? 'bg-emerald-50 border-emerald-300' : 'bg-zinc-50 border-zinc-300'}`} data-testid="dashboard-validation-banner">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {isValidated ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertCircle className="w-5 h-5 text-zinc-500" />}
          <div>
            <p className="font-semibold text-sm text-zinc-900">
              {isValidated ? 'Dashboard validé · Cyrille y a accès' : 'Dashboard en attente de votre validation (admin)'}
            </p>
            {data?.validated_by_name && (
              <p className="text-xs text-zinc-500">Validé par {data.validated_by_name} · {new Date(data.validated_at).toLocaleString('fr-FR')}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isValidated ? (
            <Button size="sm" onClick={validate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dashboard-validate-btn">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Valider pour Cyrille
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={unvalidate} disabled={busy} className="border-zinc-300" data-testid="dashboard-unvalidate-btn">
              Retirer la validation
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Bandeau Héros candidat avec validation ───
const HeroValidationBanner = ({ period }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/agent-performance/hero-of-month/proposal?period=${period}`, { headers: auth() });
      setData(r.data);
    } catch { setData(null); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/agent-performance/hero-of-month/publish`, { period }, { headers: auth() });
      toast.success('Héros publié sur la page publique');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur publication'); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/agent-performance/hero-of-month/reject`, { period, reason: 'Refusé par admin' }, { headers: auth() });
      toast.success('Publication refusée');
      await load();
    } catch (e) { toast.error('Erreur'); }
    finally { setBusy(false); }
  };
  const unpublish = async () => {
    if (!window.confirm('Retirer la publication du héros ? La page publique deviendra vide.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/agent-performance/hero-of-month/unpublish`, { period }, { headers: auth() });
      toast.success('Publication retirée');
      await load();
    } catch (e) { toast.error('Erreur'); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const role = getCurrentRole();
  const isAdmin = role === 'admin';
  const candidate = data.candidate;
  const pub = data.publication;
  const isPublished = pub?.status === 'published';
  const isRejected = pub?.status === 'rejected';

  return (
    <div className={`rounded-xl p-4 border-2 ${isPublished ? 'bg-emerald-50 border-emerald-300' : isRejected ? 'bg-zinc-50 border-zinc-300' : 'bg-amber-50 border-amber-300'}`} data-testid="hero-validation-banner">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Crown className="w-10 h-10 text-amber-500 shrink-0" />
          <div>
            <h3 className="font-bold text-zinc-900 text-base">
              Héros du mois {isPublished ? '· ✓ Publié' : isRejected ? '· ✗ Refusé' : '· En attente de validation'}
            </h3>
            {candidate ? (
              <p className="text-sm text-zinc-600 mt-0.5">
                Candidat proposé : <strong className="text-zinc-900">{candidate.first_name} {candidate.last_name || ''}</strong> — score <strong>{candidate.score?.toFixed(1)}/100</strong>
              </p>
            ) : (
              <p className="text-sm text-zinc-500 mt-0.5">Aucun candidat éligible ce mois-ci</p>
            )}
            {pub?.validated_by_name && (
              <p className="text-xs text-zinc-500 mt-0.5">
                {isPublished ? `Publié par ${pub.validated_by_name}` : `Refusé par ${pub.rejected_by_name}`}
                {' · '}{new Date(pub.validated_at || pub.rejected_at).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && candidate && !isPublished && (
            <Button size="sm" onClick={publish} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="hero-publish-btn">
              <Eye className="w-4 h-4 mr-1" /> Publier sur le site
            </Button>
          )}
          {isAdmin && candidate && !isRejected && !isPublished && (
            <Button size="sm" variant="outline" onClick={reject} disabled={busy} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="hero-reject-btn">
              <XCircle className="w-4 h-4 mr-1" /> Refuser
            </Button>
          )}
          {isAdmin && isPublished && (
            <Button size="sm" variant="outline" onClick={unpublish} disabled={busy} className="border-zinc-300 text-zinc-700 hover:bg-zinc-100" data-testid="hero-unpublish-btn">
              <EyeOff className="w-4 h-4 mr-1" /> Dépublier
            </Button>
          )}
          {!isAdmin && (
            <span className="text-xs text-zinc-500 italic">Décision admin requise</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Page principale ───
const ScoringDashboard = ({ onOpenAgent }) => {
  const role = getCurrentRole();
  const isManager = role === 'manager';
  const [period, setPeriod] = useState(previousMonthPeriod());
  const [agents, setAgents] = useState([]);
  const [scores, setScores] = useState({}); // {agent_id: {score, per_axis, ...}}
  const [previousScores, setPreviousScores] = useState({}); // pour l'évolution
  const [loading, setLoading] = useState(true);
  const [validation, setValidation] = useState(null); // {status, can_view}

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await axios.get(`${API}/agent-scoring/agents`, { headers: auth() });
      const agentsList = (list.data || []);
      setAgents(agentsList);
      // Charger les scores en parallèle pour la période courante
      const cur = await Promise.all(agentsList.map((a) =>
        axios.get(`${API}/agent-scoring/score/${a.id}?period=${period}`, { headers: auth() }).then((r) => [a.id, r.data]).catch(() => [a.id, null])
      ));
      const map = Object.fromEntries(cur.filter(([_, v]) => v));
      setScores(map);
      // Période précédente pour calcul delta
      const dt = new Date(period + '-01'); dt.setMonth(dt.getMonth() - 1);
      const prevPeriod = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      const prev = await Promise.all(agentsList.map((a) =>
        axios.get(`${API}/agent-scoring/score/${a.id}?period=${prevPeriod}`, { headers: auth() }).then((r) => [a.id, r.data]).catch(() => [a.id, null])
      ));
      setPreviousScores(Object.fromEntries(prev.filter(([_, v]) => v)));
    } catch (e) {
      toast.error('Erreur chargement dashboard');
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { reload(); }, [reload]);

  const ranking = useMemo(() => {
    const rows = agents.map((a) => {
      const s = scores[a.id];
      const prev = previousScores[a.id];
      const score = s?.score_normalized;
      const delta = (score != null && prev?.score_normalized != null) ? (score - prev.score_normalized) : null;
      return { agent: a, score, axes: s?.per_axis || {}, isCyrille: a.is_cyrille, delta, missing: s?.missing_axes || [] };
    });
    rows.sort((x, y) => {
      if (x.score == null && y.score == null) return 0;
      if (x.score == null) return 1;
      if (y.score == null) return -1;
      return y.score - x.score;
    });
    return rows;
  }, [agents, scores, previousScores]);

  const summary = useMemo(() => {
    const evalued = ranking.filter((r) => r.score != null);
    const avg = evalued.length ? evalued.reduce((s, r) => s + r.score, 0) / evalued.length : null;
    return {
      count: ranking.length,
      evaluated: evalued.length,
      avg,
      performant: evalued.filter((r) => r.score >= 80).length,
      asuivre: evalued.filter((r) => r.score >= 60 && r.score < 80).length,
      aaccompagner: evalued.filter((r) => r.score < 60).length,
    };
  }, [ranking]);

  return (
    <div className="space-y-4" data-testid="scoring-dashboard">
      {/* Header avec sélecteur période */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <Award className="w-6 h-6 text-[#D97706]" /> Dashboard Performance Agents
          </h2>
          <p className="text-sm text-zinc-500">Synthèse pour le management · classement + évolution + statut</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
            className="h-10 px-3 rounded border border-zinc-300" data-testid="dashboard-period-input" />
          <Button variant="outline" size="sm" onClick={reload} className="h-10" data-testid="dashboard-refresh">
            <RotateCcw className="w-4 h-4 mr-1" /> Rafraîchir
          </Button>
        </div>
      </div>

      {/* Bandeau validation Dashboard (admin only) */}
      <DashboardValidationBanner period={period} onChange={setValidation} />

      {/* Gate Cyrille : si pas validé, on bloque l'affichage du reste */}
      {isManager && validation && !validation.can_view ? (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6 text-center" data-testid="manager-waiting-validation">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h3 className="text-lg font-bold text-zinc-900 mb-1">En attente de validation admin</h3>
          <p className="text-sm text-zinc-600 max-w-md mx-auto">
            Le dashboard de performance pour <strong>{period}</strong> n'a pas encore été validé par l'administration.
            <br />Il sera accessible dès que l'admin l'aura approuvé.
          </p>
        </div>
      ) : (
        <>
          {/* Bandeau validation Héros */}
          <HeroValidationBanner period={period} />

      {/* KPIs synthétiques */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="kpi-evalued">
          <p className="text-xs text-zinc-500">Évalués</p>
          <p className="text-2xl font-bold text-zinc-900">{summary.evaluated}<span className="text-sm text-zinc-400">/{summary.count}</span></p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="kpi-avg">
          <p className="text-xs text-zinc-500">Moyenne équipe</p>
          <p className="text-2xl font-bold text-[#D97706]">{summary.avg != null ? summary.avg.toFixed(1) : '—'}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="kpi-perf">
          <p className="text-xs text-emerald-700">Performants</p>
          <p className="text-2xl font-bold text-emerald-700">{summary.performant}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="kpi-asuivre">
          <p className="text-xs text-amber-700">À suivre</p>
          <p className="text-2xl font-bold text-amber-700">{summary.asuivre}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid="kpi-aaccompagner">
          <p className="text-xs text-red-700">À accompagner</p>
          <p className="text-2xl font-bold text-red-700">{summary.aaccompagner}</p>
        </div>
      </div>

      {/* Tableau classement détaillé */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="ranking-table">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Agent</th>
              <th className="px-3 py-2 text-right">Score</th>
              <th className="px-3 py-2 text-center">Statut</th>
              <th className="px-3 py-2 text-center">Évolution</th>
              <th className="px-3 py-2 text-left">Détail axes (note/10)</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-zinc-400 py-6">Chargement…</td></tr>
            ) : !ranking.length ? (
              <tr><td colSpan={7} className="text-center text-zinc-400 py-6">Aucun agent à afficher</td></tr>
            ) : ranking.map((r, idx) => {
              const meta = statusMeta(r.score);
              const labels = r.isCyrille ? CYRILLE_AXIS_LABELS : AGENT_AXIS_LABELS;
              return (
                <tr key={r.agent.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`ranking-row-${r.agent.id}`}>
                  <td className="px-3 py-2 text-center font-bold text-zinc-700 w-12">
                    {r.score != null ? (idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {r.isCyrille && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                      <span className="font-medium text-zinc-900">{r.agent.name}</span>
                    </div>
                    <span className="text-xs text-zinc-400">{r.agent.role}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.score != null ? (
                      <span className="font-bold text-lg text-zinc-900">{r.score.toFixed(1)}<span className="text-xs text-zinc-400">/100</span></span>
                    ) : <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <TrendBadge delta={r.delta} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.axes || {}).slice(0, 8).map(([axe, d]) => {
                        const noteVal = typeof d?.note === 'number' ? d.note : null;
                        const weightVal = typeof d?.weight === 'number' ? d.weight : 0;
                        return (
                          <span key={axe} className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 text-[10px] whitespace-nowrap" title={`${axe}: ${noteVal != null ? noteVal : '—'}/10 (poids ${weightVal}%)`}>
                            {labels[axe] || axe.slice(0, 4)} <strong className="text-zinc-900">{noteVal != null ? noteVal.toFixed(1) : '—'}</strong>
                          </span>
                        );
                      })}
                      {r.missing?.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px]" title={`Non saisis: ${r.missing.join(', ')}`}>
                          <AlertCircle className="w-3 h-3 inline" /> {r.missing.length} non saisi(s)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => onOpenAgent?.(r.agent)} className="h-8" data-testid={`open-agent-${r.agent.id}`}>
                      Fiche <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-zinc-400 text-center">
        Les scores affichés sont basés sur les saisies dans le module Scoring & Barèmes. Les évolutions comparent au mois précédent.
      </div>
        </>
      )}
    </div>
  );
};

export default ScoringDashboard;
